import { getMessaging, getToken, isSupported, onMessage } from "@firebase/messaging";
import { useAsync, useMountEffect } from "@react-hookz/web";
import { useCallback, useEffect, useMemo } from "react";
import { isFirefox } from "react-device-detect";
import { useSdkContext } from "~/components/contexts";
import { checkIsLiveArticle } from "./helpers";
// This hook shows push notifications when messages are received when the webpage is in foreground
export const usePushNotification = app => {
  const {
    handleTrackGA,
    handleTrackGA4
  } = useSdkContext();
  const messaging = useFirebaseMessaging(app);
  useEffect(() => {
    if (!messaging) return;
    const unSubscribe = onMessage(messaging, payload => {
      const handle = async () => {
        try {
          const data = payload.data;
          if (!data || document.hidden) return;
          const {
            hashValue,
            isLiveArticle
          } = checkIsLiveArticle(data.click_action);
          handleTrackGA(isLiveArticle ? {
            action: "Push Notification/Received",
            category: "Push Notification PWA",
            label: `${data.topic},${data.nid},${data.push_time}`
          } : {
            action: "Push Notification/Received",
            category: "Live Blog Push PWA",
            label: `Live,${data.nid},${data.push_time}`
          });
          handleTrackGA4({
            action: "sys",
            category: "push",
            customized_parameters: {
              article_id: data.nid,
              push_time: data.push_time,
              section: data.topic
            },
            subcategory: "received"
          });
          if (window.Notification) {
            const notification = new Notification(data.title, {
              body: data.body,
              data,
              icon: data.icon,
              tag: data.nid
            });
            if (data.click_action) {
              notification.addEventListener("click", () => {
                handleTrackGA(isLiveArticle ? {
                  action: "Push Notification/Click to Open",
                  category: "Push Notification PWA",
                  label: `${data.topic},${data.nid},${data.push_time}`
                } : {
                  action: "Push Notification/Click to Open",
                  category: "Live Blog Push PWA",
                  label: `Live,${data.nid},${data.push_time}`
                });
                handleTrackGA4({
                  action: "click",
                  category: "push",
                  customized_parameters: {
                    article_id: data.nid,
                    push_time: data.push_time,
                    section: data.topic
                  },
                  subcategory: "received"
                });
                const url = new URL(data.click_action);
                url.searchParams.append("utm_source", "firebase_pwa");
                url.searchParams.append("utm_medium", "push_notification");
                url.searchParams.append("utm_campaign", "article");
                url.searchParams.append("utm_content", `${data.nid}${hashValue}`);
                window.open(url.href);
              });
            }
            return;
          }
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.showNotification(data.title, {
              body: data.body,
              data,
              icon: data.icon,
              tag: data.nid
            });
          }
        } catch (error) {
          console.error(new Error("Failed to handle firebase message", {
            cause: error
          }));
        }
      };
      void handle();
    });
    return unSubscribe;
  }, [handleTrackGA, handleTrackGA4, messaging]);
};
export const useIsNotificationSupported = () => {
  const [state, actions] = useAsync(async () => {
    const isNotificationApiAvailable = window !== undefined && window.Notification !== undefined;
    const isSupportFirebase = await isSupported();
    return !isFirefox && isNotificationApiAvailable && isSupportFirebase;
  });
  useMountEffect(actions.execute);
  return {
    isNotificationSupported: state
  };
};
export const useFirebaseMessaging = app => {
  const {
    isNotificationSupported
  } = useIsNotificationSupported();
  const messaging = useMemo(() => {
    if (isNotificationSupported.result !== true) return;
    if (!app) return;
    return getMessaging(app);
  }, [app, isNotificationSupported.result]);
  return messaging;
};
export const useFirebaseRegistrationToken = app => {
  const messaging = useFirebaseMessaging(app);
  const [state, actions] = useAsync(async () => {
    if (!messaging) return;
    const registration = await navigator.serviceWorker.ready;
    return getToken(messaging, {
      serviceWorkerRegistration: registration
    });
  });
  useEffect(() => {
    if (!messaging) return;
    void actions.execute();
  }, [actions, messaging]);
  const refresh = useCallback(() => {
    actions.reset();
    return actions.execute();
  }, [actions]);
  return {
    refreshRegistrationToken: refresh,
    registrationToken: state
  };
};